<template>
  <main class="sf-page">

    <!-- ── Hero ──────────────────────────────────────── -->
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner" data-aos="fade-up">
        <p class="sf-page-hero__eyebrow"><span class="sf-page-hero__eyebrow-dot"></span>Calendrier & activités</p>
        <h1 class="sf-h1">Nos <em>événements</em></h1>
        <p class="sf-page-hero__sub">Tournois, conférences, formations, sondages — rejoignez la communauté Stag Fondation</p>
      </div>
    </div>

    <!-- ── Featured ──────────────────────────────────── -->
    <section v-if="featured.length" class="sf-section sf-section--forest">
      <div class="sf-container">
        <div style="margin-bottom:2rem"><span class="sf-label sf-label--light">Événement à la une</span></div>
        <div class="ev-featured">
          <div class="ev-featured__img" :style="featured[0].featured_image?`background-image:url('${imageUrl+featured[0].featured_image}')`:''">
            <div class="ev-featured__overlay"></div>
          </div>
          <div class="ev-featured__body">
            <span class="ev-cat-badge">{{ featured[0].category }}</span>
            <h2 class="ev-featured__title">{{ featured[0].title }}</h2>
            <p class="ev-featured__desc">{{ featured[0].description }}</p>
            <div class="ev-featured__meta">
              <span class="sf-chip" style="color:rgba(245,239,224,.75)"><Calendar />{{ fmtDate(featured[0].start_date) }}</span>
              <span class="sf-chip" style="color:rgba(245,239,224,.75)"><MapPin />{{ featured[0].is_online?'En ligne':(featured[0].city||featured[0].location||'—') }}</span>
              <span v-if="featured[0].is_free" class="ev-badge-free">Gratuit</span>
              <span v-else class="ev-badge-paid">{{ featured[0].registration_fee }} {{ featured[0].fee_currency }}</span>
            </div>
            <div class="ev-featured__actions">
              <router-link :to="`/evenement/${featured[0].slug}`">
                <button class="sf-btn sf-btn--terra">Voir l'événement <ArrowRight class="sf-btn__icon" /></button>
              </router-link>
              <button v-if="featured[0].donations_enabled" class="sf-btn sf-btn--outline-light" @click="openDonateModal(featured[0])">
                <Heart class="sf-btn__icon" />Faire un don
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Filtres ────────────────────────────────────── -->
    <section class="sf-section sf-section--cream" style="padding-bottom:0">
      <div class="sf-container">
        <div class="ev-toolbar">
          <div class="sf-filters" style="margin-bottom:0;flex:1">
            <button class="sf-pill" :class="{'sf-pill--active':filters.filter===''}" @click="setFilter('filter','')">Tous</button>
            <button class="sf-pill" :class="{'sf-pill--active':filters.filter==='upcoming'}" @click="setFilter('filter','upcoming')">À venir</button>
            <button class="sf-pill" :class="{'sf-pill--active':filters.filter==='past'}" @click="setFilter('filter','past')">Passés</button>
            <button v-for="cat in categories" :key="cat" class="sf-pill" :class="{'sf-pill--active':filters.category===cat}" @click="setFilter('category',filters.category===cat?'':cat)">{{ cat }}</button>
          </div>
          <div class="sf-search-wrap">
            <Search />
            <input v-model="filters.search" @input="debounceSearch" class="sf-input" placeholder="Rechercher…" style="max-width:220px" />
          </div>
        </div>
      </div>
    </section>

    <!-- ── Grille événements ──────────────────────────── -->
    <section class="sf-section sf-section--cream">
      <div class="sf-container">
        <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>
        <template v-else>
          <div v-if="!events.length" class="sf-empty">
            <Calendar class="sf-empty__icon" />
            <p class="sf-empty__title">Aucun événement trouvé</p>
          </div>
          <div v-else class="ev-grid">
            <div v-for="(ev,i) in events" :key="ev.id" class="ev-card" data-aos="fade-up" :data-aos-delay="i*60">

              <!-- Image -->
              <div class="ev-card__media">
                <img v-if="ev.featured_image" :src="imageUrl+ev.featured_image" :alt="ev.title" loading="lazy" />
                <div v-else class="ev-card__media-fallback"><Calendar /></div>
                <span class="ev-card__cat">{{ ev.category }}</span>
                <span v-if="ev.is_free" class="ev-card__free">Gratuit</span>
                <span v-else class="ev-card__price">{{ ev.registration_fee }} {{ ev.fee_currency }}</span>
              </div>

              <!-- Corps -->
              <div class="ev-card__body">
                <!-- Date bandeau -->
                <div class="ev-card__date-row">
                  <div class="ev-date-box">
                    <span class="ev-date-box__day">{{ new Date(ev.start_date).getDate() }}</span>
                    <span class="ev-date-box__month">{{ new Date(ev.start_date).toLocaleString('fr-FR',{month:'short'}).toUpperCase() }}</span>
                  </div>
                  <div>
                    <p class="ev-card__time">{{ fmtTime(ev.start_date) }}<span v-if="ev.end_date"> → {{ fmtTime(ev.end_date) }}</span></p>
                    <p class="ev-card__loc"><MapPin />{{ ev.is_online?'En ligne':(ev.city||ev.location||'—') }}</p>
                  </div>
                </div>

                <h3 class="ev-card__title">{{ ev.title }}</h3>
                <p class="ev-card__desc">{{ ev.excerpt || ev.description?.slice(0,120) }}…</p>

                <!-- Jauge participation -->
                <div v-if="ev.capacity" class="ev-card__capacity">
                  <div style="display:flex;justify-content:space-between;font-size:.75rem;color:var(--ink-60);margin-bottom:.3rem">
                    <span>{{ ev.registered_count||0 }} inscrits</span>
                    <span>{{ ev.capacity - (ev.registered_count||0) }} places restantes</span>
                  </div>
                  <div class="sf-progress__track"><div class="sf-progress__fill" :style="{width:Math.min(100,(ev.registered_count||0)/ev.capacity*100)+'%'}"></div></div>
                </div>

                <!-- Objectif de dons -->
                <div v-if="ev.donations_enabled && ev.donation_goal" class="ev-card__donation-bar">
                  <div style="display:flex;justify-content:space-between;font-size:.75rem;color:var(--ink-60);margin-bottom:.3rem">
                    <span><Heart style="width:11px;height:11px;display:inline" /> {{ fmtEur(ev.donation_collected) }} collectés</span>
                    <span>sur {{ fmtEur(ev.donation_goal) }}</span>
                  </div>
                  <div class="sf-progress__track"><div class="sf-progress__fill" style="background:linear-gradient(90deg,var(--terra),var(--amber))" :style="{width:Math.min(100,(ev.donation_collected||0)/ev.donation_goal*100)+'%'}"></div></div>
                </div>

                <!-- Actions -->
                <div class="ev-card__actions">
                  <router-link :to="`/evenement/${ev.slug}`" style="flex:1">
                    <button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">Détails</button>
                  </router-link>
                  <button v-if="ev.registration_required && ev.status==='published'" class="sf-btn sf-btn--terra sf-btn--sm" style="flex:1" :disabled="ev.registered_count>=ev.capacity && ev.capacity" @click="openRegisterModal(ev)">
                    {{ ev.registered_count>=ev.capacity && ev.capacity ? 'Complet' : "S'inscrire" }}
                  </button>
                  <button v-if="ev.donations_enabled" class="sf-btn sf-btn--amber sf-btn--sm" @click="openDonateModal(ev)" title="Faire un don"><Heart /></button>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.last_page > 1" class="sf-pagination">
            <button class="sf-page-btn" :disabled="pagination.current_page===1" @click="changePage(pagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="sf-page-btn" :class="{'sf-page-btn--active':pagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="sf-page-btn" :disabled="pagination.current_page===pagination.last_page" @click="changePage(pagination.current_page+1)"><ChevronRight /></button>
          </div>
        </template>
      </div>
    </section>

    <!-- ══ MODAL : Inscription ════════════════════════ -->
    <Transition name="modal-fade">
      <div v-if="showRegModal" class="ev-modal-overlay" @click.self="showRegModal=false">
        <div class="ev-modal">
          <div class="ev-modal__head">
            <h3>S'inscrire à « {{ regEvent?.title }} »</h3>
            <button @click="showRegModal=false"><X /></button>
          </div>
          <form @submit.prevent="submitRegistration" class="ev-modal__body">
            <div class="ev-modal__info">
              <span class="sf-chip"><Calendar />{{ fmtDate(regEvent?.start_date) }}</span>
              <span class="sf-chip"><MapPin />{{ regEvent?.is_online?'En ligne':regEvent?.city }}</span>
              <span v-if="regEvent?.is_free" class="ev-badge-free">Gratuit</span>
              <span v-else class="ev-badge-paid">{{ regEvent?.registration_fee }} {{ regEvent?.fee_currency }}</span>
            </div>
            <div class="ev-form-grid">
              <div class="ev-field"><label>Prénom <span>*</span></label><input v-model="regForm.first_name" required class="sf-input" placeholder="Votre prénom" /></div>
              <div class="ev-field"><label>Nom <span>*</span></label><input v-model="regForm.last_name" required class="sf-input" placeholder="Votre nom" /></div>
              <div class="ev-field" style="grid-column:span 2"><label>Email <span>*</span></label><input v-model="regForm.email" required type="email" class="sf-input" placeholder="votre@email.com" /></div>
              <div class="ev-field"><label>Téléphone</label><input v-model="regForm.phone" class="sf-input" placeholder="+237 6…" /></div>
              <div class="ev-field"><label>Organisation</label><input v-model="regForm.organisation" class="sf-input" placeholder="École, association…" /></div>
            </div>
            <p v-if="regError" class="ev-error">{{ regError }}</p>
          </form>
          <div class="ev-modal__foot">
            <button class="sf-btn sf-btn--outline" @click="showRegModal=false">Annuler</button>
            <button class="sf-btn sf-btn--terra" :disabled="regLoading" @click="submitRegistration">
              <span v-if="regLoading" class="sf-loader__ring" style="width:16px;height:16px;border-width:2px"></span>
              Confirmer l'inscription
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ══ MODAL : Don lié à un événement ════════════ -->
    <Transition name="modal-fade">
      <div v-if="showDonateModal" class="ev-modal-overlay" @click.self="showDonateModal=false">
        <div class="ev-modal">
          <div class="ev-modal__head">
            <h3>Soutenir « {{ donateEvent?.title }} »</h3>
            <button @click="showDonateModal=false"><X /></button>
          </div>
          <div class="ev-modal__body">
            <!-- Barre objectif -->
            <div v-if="donateEvent?.donation_goal" class="ev-donate-progress">
              <div style="display:flex;justify-content:space-between;margin-bottom:.5rem">
                <span style="font-weight:600;font-size:.95rem;color:var(--forest)">{{ fmtEur(donateEvent.donation_collected) }}</span>
                <span style="font-size:.82rem;color:var(--ink-60)">Objectif : {{ fmtEur(donateEvent.donation_goal) }}</span>
              </div>
              <div class="sf-progress__track" style="height:8px"><div class="sf-progress__fill" style="background:linear-gradient(90deg,var(--terra),var(--amber))" :style="{width:Math.min(100,(donateEvent.donation_collected||0)/donateEvent.donation_goal*100)+'%'}"></div></div>
              <p style="font-size:.78rem;color:var(--ink-60);margin-top:.4rem">{{ Math.min(100,Math.round((donateEvent.donation_collected||0)/donateEvent.donation_goal*100)) }}% de l'objectif atteint</p>
            </div>

            <p style="font-size:.88rem;color:var(--ink-60);line-height:1.65;margin-bottom:1.25rem">
              Votre don soutient directement cet événement et les actions de Stag Fondation pour l'inclusion numérique à Douala.
            </p>

            <!-- Redirect vers la page de don avec le projet lié -->
            <div style="display:flex;flex-direction:column;gap:.75rem">
              <router-link :to="`/faire-un-don?event=${donateEvent?.id}`">
                <button class="sf-btn sf-btn--terra sf-btn--full">
                  <Heart class="sf-btn__icon" /> Faire un don pour cet événement
                </button>
              </router-link>
              <button class="sf-btn sf-btn--outline sf-btn--full" @click="showDonateModal=false">Plus tard</button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Toast -->
    <Transition name="toast-fade">
      <div v-if="toast.msg" class="sf-toast"><component :is="toast.type==='error'?'X':'CheckCircle'" />{{ toast.msg }}</div>
    </Transition>

  </main>
</template>

<script>
import axios from 'axios'
import { debounce } from 'lodash'
import AOS from 'aos'; import 'aos/dist/aos.css'
import {
  Calendar, CalendarDays, MapPin, Heart, ArrowRight, Search,
  ChevronLeft, ChevronRight, X, CheckCircle, Users
} from 'lucide-vue-next'

const PUB = () => axios.create({ baseURL: 'https://api.stagfondation.org/api/public' })

export default {
  name: 'EventsPage',
  components: { Calendar, CalendarDays, MapPin, Heart, ArrowRight, Search, ChevronLeft, ChevronRight, X, CheckCircle, Users },

  data() {
    return {
      imageUrl: 'https://api.stagfondation.org/',
      loading: true,
      events: [], featured: [],
      categories: [],
      pagination: { current_page:1, last_page:1, total:0 },
      displayedPages: [],
      filters: { search:'', filter:'', category:'', page:1 },

      // Inscription
      showRegModal: false, regEvent: null, regLoading: false, regError: '',
      regForm: { first_name:'', last_name:'', email:'', phone:'', organisation:'' },

      // Don
      showDonateModal: false, donateEvent: null,
      toast: { msg:'', type:'success' }
    }
  },

  async created() {
    this.debounceSearch = debounce(this.loadEvents, 450)
    await Promise.all([this.loadFeatured(), this.loadCategories(), this.loadEvents()])
  },

  mounted() {
    AOS.init({ once:true, offset:70, easing:'ease-out-cubic', duration:650 })
  },

  methods: {
    async loadEvents() {
      this.loading = true
      try {
        const { data } = await PUB().get('/events', { params: this.filters })
        this.events = data.data
        this.pagination = data.meta
        this.updatePages()
      } catch(e) { this.showToast('Erreur de chargement','error') }
      finally { this.loading = false }
    },
    async loadFeatured() {
      try { const { data } = await PUB().get('/events/featured'); this.featured = data.data } catch {}
    },
    async loadCategories() {
      try { const { data } = await PUB().get('/events/categories'); this.categories = data.data } catch {}
    },

    setFilter(key, val) { this.filters[key]=val; this.filters.page=1; this.loadEvents() },
    changePage(p) { if(p>=1&&p<=this.pagination.last_page){this.filters.page=p;this.loadEvents()} },
    updatePages() {
      const c=this.pagination.current_page, l=this.pagination.last_page, d=2, pages=[]
      for(let i=1;i<=l;i++) if(i===1||i===l||(i>=c-d&&i<=c+d)) pages.push(i)
      this.displayedPages=pages
    },

    // Inscription
    openRegisterModal(ev) {
      this.regEvent=ev; this.regError=''; this.regLoading=false
      this.regForm={ first_name:'', last_name:'', email:'', phone:'', organisation:'' }
      this.showRegModal=true
    },
    async submitRegistration() {
      this.regLoading=true; this.regError=''
      try {
        const { data } = await PUB().post(`/events/${this.regEvent.id}/register`, this.regForm)
        this.showToast(data.message)
        this.showRegModal=false
        this.loadEvents()
      } catch(e) {
        this.regError = e.response?.data?.message || 'Erreur lors de l\'inscription.'
      } finally { this.regLoading=false }
    },

    openDonateModal(ev) { this.donateEvent=ev; this.showDonateModal=true },

    fmtDate(d) { if(!d)return'—'; return new Date(d).toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'}) },
    fmtTime(d) { if(!d)return''; return new Date(d).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}) },
    fmtEur(v) { if(!v)return'0 €'; return Number(v).toLocaleString('fr-FR')+(v>100?' €':' €') },
    showToast(msg,type='success') { this.toast={msg,type}; setTimeout(()=>{this.toast={msg:'',type:'success'}},4000) }
  }
}
</script>

<style>@import './sf-design.css';</style>
<style scoped>
/* ── Featured ────────────────────────────────────── */
.ev-featured {
  display:grid; grid-template-columns:1fr 1fr; gap:0;
  border-radius:12px; overflow:hidden; min-height:360px;
}
@media(max-width:768px) { .ev-featured { grid-template-columns:1fr; } }
.ev-featured__img { position:relative; background:var(--forest-l) center/cover no-repeat; min-height:240px; }
.ev-featured__overlay { position:absolute; inset:0; background:linear-gradient(135deg,rgba(27,58,45,.6),rgba(27,58,45,.2)); }
.ev-featured__body { background:rgba(255,255,255,.07); backdrop-filter:blur(12px); border:1px solid rgba(255,255,255,.12); padding:2.5rem 2rem; display:flex; flex-direction:column; gap:1rem; justify-content:center; }
.ev-featured__title { font-family:var(--fd); font-size:clamp(1.5rem,3vw,2rem); font-weight:600; color:var(--white); line-height:1.15; }
.ev-featured__desc { font-size:.88rem; color:rgba(245,239,224,.75); line-height:1.65; }
.ev-featured__meta { display:flex; flex-wrap:wrap; gap:.65rem; }
.ev-featured__actions { display:flex; gap:.75rem; flex-wrap:wrap; margin-top:.5rem; }

/* ── Category badge ──────────────────────────────── */
.ev-cat-badge {
  display:inline-block; background:var(--forest); color:var(--cream);
  font-size:.68rem; letter-spacing:.1em; text-transform:uppercase;
  padding:.3rem .7rem; border-radius:2px;
}

/* ── Toolbar ─────────────────────────────────────── */
.ev-toolbar { display:flex; align-items:center; gap:1rem; flex-wrap:wrap; }
@media(max-width:640px) { .ev-toolbar { flex-direction:column; align-items:flex-start; } }

/* ── Event grid ──────────────────────────────────── */
.ev-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:1.5rem; }
@media(max-width:480px) { .ev-grid { grid-template-columns:1fr; } }

/* ── Event card ──────────────────────────────────── */
.ev-card { background:var(--white); border-radius:10px; overflow:hidden; box-shadow:var(--shadow-sm); transition:transform .3s ease,box-shadow .3s ease; display:flex; flex-direction:column; }
.ev-card:hover { transform:translateY(-4px); box-shadow:var(--shadow-md); }
.ev-card__media { position:relative; height:190px; overflow:hidden; background:var(--cream-d); }
.ev-card__media img { width:100%; height:100%; object-fit:cover; transition:transform .6s ease; }
.ev-card:hover .ev-card__media img { transform:scale(1.06); }
.ev-card__media-fallback { width:100%; height:100%; display:flex; align-items:center; justify-content:center; color:var(--sage); }
.ev-card__media-fallback :deep(svg) { width:48px; height:48px; }
.ev-card__cat { position:absolute; top:1rem; left:1rem; background:var(--forest); color:var(--cream); font-size:.67rem; letter-spacing:.1em; text-transform:uppercase; padding:.3rem .7rem; border-radius:2px; }
.ev-card__free { position:absolute; top:1rem; right:1rem; background:var(--green); color:var(--white); font-size:.67rem; font-weight:700; text-transform:uppercase; padding:.3rem .7rem; border-radius:2px; }
.ev-card__price { position:absolute; top:1rem; right:1rem; background:var(--amber); color:var(--white); font-size:.72rem; font-weight:700; padding:.3rem .7rem; border-radius:2px; }
.ev-card__body { padding:1.5rem; display:flex; flex-direction:column; gap:.85rem; flex:1; }
.ev-card__date-row { display:flex; align-items:center; gap:.85rem; }
.ev-date-box { background:var(--cream); border-radius:6px; padding:.4rem .7rem; text-align:center; flex-shrink:0; min-width:48px; }
.ev-date-box__day { display:block; font-family:var(--fd); font-size:1.6rem; font-weight:700; color:var(--terra); line-height:1; }
.ev-date-box__month { display:block; font-size:.65rem; font-weight:600; color:var(--ink-60); letter-spacing:.06em; }
.ev-card__time { font-size:.78rem; font-weight:600; color:var(--forest); }
.ev-card__loc { display:flex; align-items:center; gap:.3rem; font-size:.76rem; color:var(--ink-60); margin-top:.15rem; }
.ev-card__loc :deep(svg) { width:12px; height:12px; flex-shrink:0; }
.ev-card__title { font-family:var(--fd); font-size:1.15rem; font-weight:600; color:var(--ink); line-height:1.25; }
.ev-card__desc { font-size:.84rem; color:var(--ink-60); line-height:1.6; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.ev-card__capacity { margin-top:auto; }
.ev-card__donation-bar { }
.ev-card__actions { display:flex; gap:.5rem; align-items:center; margin-top:auto; }
.ev-card__actions .sf-btn--amber { padding:.5rem .65rem; }

/* Free / paid badges */
.ev-badge-free { background:rgba(16,185,129,.15); color:#065f46; font-size:.72rem; font-weight:600; padding:.25rem .65rem; border-radius:999px; }
.ev-badge-paid { background:rgba(232,147,58,.15); color:#92400e; font-size:.72rem; font-weight:600; padding:.25rem .65rem; border-radius:999px; }

/* ── Modal ───────────────────────────────────────── */
.ev-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:200; padding:1rem; }
.ev-modal { background:var(--white); border-radius:10px; box-shadow:0 20px 60px rgba(0,0,0,.2); max-width:560px; width:100%; max-height:90vh; display:flex; flex-direction:column; }
.ev-modal__head { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:1px solid var(--ink-12); }
.ev-modal__head h3 { font-family:var(--fd); font-size:1.15rem; font-weight:600; color:var(--ink); }
.ev-modal__head button { background:var(--cream); border:none; border-radius:4px; width:28px; height:28px; display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--ink-60); }
.ev-modal__head button :deep(svg) { width:14px; height:14px; }
.ev-modal__body { padding:1.5rem; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:1rem; }
.ev-modal__foot { padding:1rem 1.5rem; border-top:1px solid var(--ink-12); display:flex; justify-content:flex-end; gap:.6rem; }
.ev-modal__info { display:flex; flex-wrap:wrap; gap:.5rem; margin-bottom:.25rem; }

/* Formulaire inscriptions */
.ev-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:.85rem; }
@media(max-width:480px) { .ev-form-grid { grid-template-columns:1fr; } }
.ev-field { display:flex; flex-direction:column; gap:.3rem; }
.ev-field label { font-size:.8rem; font-weight:500; color:var(--ink); }
.ev-field label span { color:var(--terra); }
.ev-error { font-size:.82rem; color:var(--terra); background:rgba(200,90,42,.07); padding:.65rem 1rem; border-radius:4px; }

/* Don barre */
.ev-donate-progress { background:var(--cream); border-radius:8px; padding:1.25rem; margin-bottom:.75rem; }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition:opacity .25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity:0; }
.toast-fade-enter-active, .toast-fade-leave-active { transition:opacity .3s ease, transform .3s ease; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity:0; transform:translateY(8px); }
</style>